import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    TextField, Box, FormControl, InputLabel, FormHelperText, Button, Select,
    MenuItem, RadioGroup, FormControlLabel, Radio, Tooltip
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import FormSkeleton from '@components/FormSkeleton';
import WorkflowSelect from '@components/workflow/WorkflowSelect';
import MailingListSelect from '@components/mailinglist/MailingListSelect';

const SendoutCreationForm = forwardRef(({ formData, onSubmit, fieldsVariant = "standard" }, ref) => {
    const { t, i18n } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [status, setStatus] = useState(formData?.status || null);

    const validationSchema = Yup.object({
        workflow_id: Yup.string()
            .required(t('This field is required')),
        mailing_list_id: Yup.string()
            .required(t('This field is required')),
        status: Yup.string()
            .required(t('This field is required')),
        is_test: Yup.boolean()
            .required(t('This field is required')),
        planned_at: Yup.date()
            .typeError(t('The planned at field must be a valid date.'))
            .test('planned-at-after-now', t('The planned at field must be a date after now.'), function(value) {
                if (this.parent.status === 'Planned') {
                    return value > new Date();
                }
                return true;
            })
            .when('status', {
                is: 'Planned',
                then: (schema) => schema.required(t('This field is required'))
            })
    });

    const { control, handleSubmit, setValue, setError, formState: { errors, isValid }, watch } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onBlur',
    });
    useImperativeHandle(ref, () => ({ setError })); // Permettre au composant parent d'appeler setError

    // Surveiller les changements du statut dans le formulaire
    const watchedStatus = watch('status');
    useEffect(() => {
        if (watchedStatus) {
            setStatus(watchedStatus);
        }
    }, [watchedStatus]);

    useEffect(() => {
        if (formData) {
            for (const [key, value] of Object.entries(formData)) {
                setValue(key, value);
            }
        }
        setLoaded(true);
    }, [formData, setValue]);

    if (!loaded) return <FormSkeleton nb={4} />;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>

            <Box mb={3} pt={2}>
                <Controller
                    name="workflow_id"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.workflow_id)}
                        >
                            <InputLabel>{t('Workflow')}</InputLabel>
                            <WorkflowSelect
                                {...field}
                            />
                            {errors.workflow_id ? <FormHelperText>{errors.workflow_id.message}</FormHelperText> : ''}
                        </FormControl>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="mailing_list_id"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.mailing_list_id)}
                        >
                            <InputLabel>{t('Mailing list')}</InputLabel>
                            <MailingListSelect
                                {...field}
                            />
                            {errors.mailing_list_id ? <FormHelperText>{errors.mailing_list_id.message}</FormHelperText> : ''}
                        </FormControl>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.status)}
                        >
                            <InputLabel>{t('Status')}</InputLabel>
                            <Select
                                {...field}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    field.onChange(e);
                                }}
                            >
                                <MenuItem key="InProgress" value="InProgress">{t('Start right now')}</MenuItem>
                                <MenuItem key="Paused" value="Paused">{t('Do not start right now (paused)')}</MenuItem>
                                <MenuItem key="Planned" value="Planned">{t('Start at a planned date')}...</MenuItem>
                            </Select>
                            {errors.status ? <FormHelperText>{errors.status.message}</FormHelperText> : ''}
                        </FormControl>
                    )}
                />
            </Box>

            {status === 'Planned' && (
                <Box mb={3}>
                    <Controller
                        name="planned_at"
                        control={control}
                        render={({ field }) => {
                            // S'assurer que la valeur est un DateTime valide ou null
                            const value = field.value 
                                ? (field.value instanceof DateTime 
                                    ? field.value 
                                    : DateTime.fromJSDate(new Date(field.value)))
                                : null;
                            
                            return (
                                <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                                    <DateTimePicker
                                        {...field}
                                        value={value}
                                        onChange={(newValue) => {
                                            field.onChange(newValue);
                                        }}
                                        ampm={false}
                                        label={t('Planned date')}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: fieldsVariant,
                                                error: Boolean(errors.planned_at),
                                                helperText: errors.planned_at?.message
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            );
                        }}
                    />
                </Box>
            )}

            <Box mb={3}>
                <Controller
                    name="is_test"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            variant={fieldsVariant}
                        >
                            <RadioGroup
                                row
                                {...field}
                            >
                                <Tooltip title={t('In test mode, the workflow will be executed without sending emails or SMS.')} placement="top" arrow>
                                    <FormControlLabel value={true} control={<Radio />} label={t('Test mode')} />
                                </Tooltip>
                                <Tooltip title={t('Execute the workflow and send emails and SMS.')} placement="top" arrow>
                                    <FormControlLabel value={false} control={<Radio />} label={t('Real sendout')} />
                                </Tooltip>
                            </RadioGroup>
                        </FormControl>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            multiline
                            label={t('Description')}
                            variant={fieldsVariant}
                        />
                    )}
                />
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="submit"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    color="primary"
                    disabled={!isValid}
                >
                    {t('Next')}
                </Button>
            </Box>
        </form>
    );
});

export default SendoutCreationForm;
